import React, { useState, useRef, useEffect } from 'react';
import { syncApi } from '../services/api';
import { useNavigate } from 'react-router-dom';
import Button from '../components/common/Button';
import toast from 'react-hot-toast';

const TimesheetRollback = () => {
    const [jobNumber, setJobNumber] = useState('');
    const [timesheets, setTimesheets] = useState([]);
    const [selectedUuids, setSelectedUuids] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [rollbackStatus, setRollbackStatus] = useState(null); // 'pending' | 'success' | 'failed' | null
    const pollIntervalRef = useRef(null);
    const pollStartTimeRef = useRef(null);
    const navigate = useNavigate();

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        };
    }, []);

    const handleSearch = async () => {
        if (!jobNumber.trim()) return;

        setSearching(true);
        setError(null);
        setMessage(null);
        setTimesheets([]);
        setSelectedUuids(new Set());
        setRollbackStatus(null);

        try {
            const result = await syncApi.getExportedTimesheets(jobNumber.trim());
            if (result.success) {
                setTimesheets(result.timesheets || []);
                if (result.timesheets.length === 0) {
                    setMessage('No exported timesheets found for job ' + jobNumber);
                }
            } else {
                setError(result.message || 'Failed to search');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error searching timesheets');
        } finally {
            setSearching(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') handleSearch();
    };

    const toggleSelect = (uuid) => {
        setSelectedUuids(prev => {
            const next = new Set(prev);
            if (next.has(uuid)) {
                next.delete(uuid);
            } else {
                next.add(uuid);
            }
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedUuids.size === timesheets.length) {
            setSelectedUuids(new Set());
        } else {
            setSelectedUuids(new Set(timesheets.map(t => t.uuid)));
        }
    };

    const startPollingForResult = () => {
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);

        setRollbackStatus('pending');
        pollStartTimeRef.current = Date.now();

        pollIntervalRef.current = setInterval(async () => {
            try {
                const response = await syncApi.getStatus();
                if (response.success && response.data?.recentLogs?.length > 0) {
                    const latestLog = response.data.recentLogs[0];
                    const logTime = new Date(latestLog.createdAt).getTime();

                    if (logTime > pollStartTimeRef.current &&
                        latestLog.commandType === 'rollback_timesheets' &&
                        (latestLog.status === 'success' || latestLog.status === 'failed' || latestLog.status === 'completed')) {

                        // Stop polling
                        clearInterval(pollIntervalRef.current);
                        pollIntervalRef.current = null;

                        if (latestLog.status === 'success' || latestLog.status === 'completed') {
                            setRollbackStatus('success');
                            setMessage(latestLog.message || 'Rollback completed successfully.');
                            toast.success('Rollback completed!');
                        } else {
                            setRollbackStatus('failed');
                            setError(latestLog.message || 'Rollback failed.');
                            toast.error('Rollback failed.');
                        }
                    }
                }
            } catch (err) {
                console.error('Error polling rollback status:', err);
            }
        }, 2000);

        // Stop polling after 2 minutes
        setTimeout(() => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
                if (rollbackStatus === 'pending') {
                    setRollbackStatus(null);
                    setMessage('Rollback is taking longer than expected. Check the Sync Dashboard for status.');
                }
            }
        }, 120000);
    };

    const handleRollback = async () => {
        if (selectedUuids.size === 0) return;

        const selectedTimesheets = timesheets.filter(t => selectedUuids.has(t.uuid));
        const referenceNumbers = selectedTimesheets.map(t => t.referenceNo);
        const uuids = selectedTimesheets.map(t => t.uuid);

        const confirmMsg = `Are you sure you want to rollback ${uuids.length} timesheet(s)?\n\n` +
            `Reference Numbers: ${referenceNumbers.join(', ')}\n` +
            `Job Number: ${jobNumber}\n\n` +
            `This will remove the line items from the QuickBooks estimate and mark the timesheets as un-exported.`;

        if (!window.confirm(confirmMsg)) return;

        setLoading(true);
        setError(null);
        setMessage(null);
        setRollbackStatus(null);

        try {
            const result = await syncApi.rollbackTimesheets(selectedTimesheets[0].workOrderNumber, uuids, referenceNumbers);
            if (result.success) {
                setMessage('Rollback command queued. Waiting for agent to process...');
                // Remove rolled-back timesheets from the list
                setTimesheets(prev => prev.filter(t => !selectedUuids.has(t.uuid)));
                setSelectedUuids(new Set());
                // Start polling for completion
                startPollingForResult();
            } else {
                setError(result.message || 'Rollback failed');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error triggering rollback');
        } finally {
            setLoading(false);
        }
    };

    const getTsTypeLabel = (tsType) => {
        switch (tsType) {
            case '1': return 'Repair In Shop';
            case '2': return 'Packing Slip';
            case '3': return 'Standard';
            case '4': return 'Pickup Slip';
            case '5': return 'Vendor Slip';
            default: return 'Standard';
        }
    };

    const getTypeBadge = (tsType) => {
        const types = {
            '0': { label: 'Timesheet', cls: 'bg-blue-100 text-blue-800' },
            '1': { label: 'Repair', cls: 'bg-purple-100 text-purple-800' },
            '2': { label: 'Packing', cls: 'bg-green-100 text-green-800' },
            '3': { label: 'Internal', cls: 'bg-gray-100 text-gray-800' },
            '4': { label: 'Pickup', cls: 'bg-yellow-100 text-yellow-800' },
            '5': { label: 'Vendor', cls: 'bg-orange-100 text-orange-800' },
        };
        const t = types[tsType] || { label: 'Unknown', cls: 'bg-gray-100' };
        return <span className={`text-xs px-2 py-0.5 rounded ${t.cls}`}>{t.label}</span>;
    };

    return (
        <div>
            {/* Header */}
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">Timesheet Rollback</h1>
                    <p className="text-blue-100 mt-1">Remove exported timesheets from QuickBooks estimates</p>
                </div>
                <Button variant="secondary" onClick={() => navigate('/admin/sync')}>
                    ← Back to Sync Dashboard
                </Button>
            </div>

            {/* Search Bar */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                <p className="text-gray-600 mb-4">
                    Search for exported timesheets by job number to remove them from the QuickBooks estimate.
                    After rollback, timesheets return to draft status for editing and re-export.
                </p>
                <div className="flex gap-3">
                    <input
                        type="text"
                        value={jobNumber}
                        onChange={(e) => setJobNumber(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Enter job / work order number..."
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                    />
                    <Button
                        onClick={handleSearch}
                        loading={searching}
                        disabled={!jobNumber.trim()}
                    >
                        {searching ? 'Searching...' : 'Search'}
                    </Button>
                </div>
            </div>

            {/* Status Messages */}
            {message && (
                <div className={`p-4 mb-4 rounded-lg border ${rollbackStatus === 'success'
                    ? 'bg-green-50 text-green-800 border-green-300'
                    : 'bg-green-50 text-green-800 border-green-300'
                    }`}>
                    {message}
                </div>
            )}

            {error && (
                <div className="p-4 mb-4 rounded-lg border bg-red-50 text-red-800 border-red-300">
                    {error}
                </div>
            )}

            {/* Rollback In Progress Banner */}
            {rollbackStatus === 'pending' && (
                <div className="p-4 mb-4 rounded-lg border bg-blue-50 text-blue-800 border-blue-300 flex items-center gap-3">
                    <div className="spinner w-5 h-5"></div>
                    <span className="font-medium animate-pulse">
                        Rollback in progress... waiting for agent to finish processing.
                    </span>
                </div>
            )}

            {/* Results */}
            {timesheets.length > 0 && (
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    {/* Results Header */}
                    <div className="px-4 py-3 bg-gray-50 border-b flex justify-between items-center">
                        <span className="font-semibold text-gray-800">
                            {timesheets.length} exported timesheet(s) found — {selectedUuids.size} selected
                        </span>
                        <Button
                            variant="danger"
                            onClick={handleRollback}
                            loading={loading || rollbackStatus === 'pending'}
                            disabled={selectedUuids.size === 0}
                        >
                            {loading ? 'Rolling Back...' : rollbackStatus === 'pending' ? 'Processing...' : `Rollback Selected (${selectedUuids.size})`}
                        </Button>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-center w-10">
                                        <input
                                            type="checkbox"
                                            checked={selectedUuids.size === timesheets.length && timesheets.length > 0}
                                            onChange={toggleSelectAll}
                                            className="w-4 h-4 cursor-pointer"
                                        />
                                    </th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Ref #</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Type</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Customer</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Technician</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Date</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Work Performed</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {timesheets.map((ts) => (
                                    <tr
                                        key={ts.uuid}
                                        onClick={() => toggleSelect(ts.uuid)}
                                        className={`cursor-pointer transition-colors ${selectedUuids.has(ts.uuid) ? 'bg-blue-50' : 'hover:bg-gray-50'
                                            }`}
                                    >
                                        <td className="px-4 py-3 text-center">
                                            <input
                                                type="checkbox"
                                                checked={selectedUuids.has(ts.uuid)}
                                                onChange={() => toggleSelect(ts.uuid)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-4 h-4 cursor-pointer"
                                            />
                                        </td>
                                        <td className="px-4 py-3 font-semibold">{ts.referenceNo}</td>
                                        <td className="px-4 py-3">{getTypeBadge(ts.tsType)}</td>
                                        <td className="px-4 py-3 text-sm">{ts.customer}</td>
                                        <td className="px-4 py-3 text-sm">{ts.technicianName}</td>
                                        <td className="px-4 py-3 text-sm">{ts.month}</td>
                                        <td className="px-4 py-3 text-sm max-w-[250px] truncate">{ts.workPerformed}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimesheetRollback;